import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { ParticleBackground } from '../animations/ParticleBackground';
import { WarpSpeedTunnel } from '../animations/WarpSpeedTunnel';
import { useUIStore } from '../../stores/uiStore';
import { useEasterEgg } from '../../hooks/useEasterEgg';

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );

  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  return isDesktop;
}

export function ClientLayout() {
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const { sidebarCollapsed } = useUIStore();
  const { isTrippyMode } = useEasterEgg();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Sidebar */}
      <Sidebar />
      {/* Background animations - Easter egg: type "trippy" to toggle warp speed */}
      <AnimatePresence mode="wait">
        {isTrippyMode ? (
          <motion.div
            key="warp"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <WarpSpeedTunnel />
          </motion.div>
        ) : (
          <motion.div
            key="particles"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ParticleBackground />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Animated gradient mesh background */}
      <div className="fixed inset-0 -z-10 pointer-events-none overflow-hidden">
        {/* Primary floating orb */}
        <motion.div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.08) 50%, transparent 70%)',
          }}
          animate={{
            x: [0, 80, 20, 0],
            y: [0, 40, 80, 0],
            scale: [1, 1.2, 0.9, 1],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Secondary floating orb */}
        <motion.div
          className="absolute -bottom-60 -left-60 w-[700px] h-[700px] rounded-full blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, rgba(99, 102, 241, 0.06) 50%, transparent 70%)',
          }}
          animate={{
            x: [0, -60, 30, 0],
            y: [0, -50, -20, 0],
            scale: [1, 1.15, 1.05, 1],
          }}
          transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Central rotating gradient */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl"
          style={{
            background: 'conic-gradient(from 0deg, rgba(99, 102, 241, 0.08), rgba(168, 85, 247, 0.08), rgba(236, 72, 153, 0.05), rgba(99, 102, 241, 0.08))',
          }}
          animate={{
            rotate: [0, 360],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
        />

        {/* Accent pulse */}
        <motion.div
          className="absolute top-1/4 right-1/4 w-[300px] h-[300px] rounded-full blur-2xl"
          style={{
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 60%)',
          }}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Floating accent spot */}
        <motion.div
          className="absolute bottom-1/3 left-1/4 w-[200px] h-[200px] rounded-full blur-xl"
          style={{
            background: 'radial-gradient(circle, rgba(236, 72, 153, 0.15) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, 100, -50, 0],
            y: [0, -80, 40, 0],
            scale: [1, 1.3, 0.8, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Grid overlay for depth */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(99, 102, 241, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(99, 102, 241, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />

        {/* Radial vignette - dark mode only */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.3) 100%)',
          }}
        />
      </div>

      {/* Main content wrapper with sidebar margin */}
      <motion.div
        animate={{ marginLeft: isDesktop ? (sidebarCollapsed ? 0 : 256) : 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="min-h-screen flex flex-col"
      >
        <main className="flex-1 px-4 py-8 lg:px-8 xl:px-12 relative z-10 flex flex-col min-h-0">
          <div className="max-w-[1800px] mx-auto w-full flex-1 flex flex-col min-h-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 30, scale: 0.98, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -20, scale: 0.98, filter: 'blur(4px)' }}
                transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="flex-1 flex flex-col min-h-0"
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </motion.div>
    </div>
  );
}
